import { useEffect, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate } from 'react-router-dom';
import { startOutboxWatchdog } from './offline/sync';
import { requestPersistentStorage } from './offline/persistentStorage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SessionActivityTracker } from '@/components/SessionActivityTracker';
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog';
import { SessionExpiryWarning } from '@/components/SessionExpiryWarning';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { LoggedOut } from './pages/LoggedOut';
import { HomeDashboard } from './pages/HomeDashboard';
import { Layout } from './components/Layout';
import { appRoutes } from './routes/index.custom';
import { createAppTheme, type AccentColorKey } from './theme';
import { CACHE_TIME } from './config/queryDefaults';
import { UserPreferencesProvider, useUserPreferences } from './contexts/UserPreferencesContext';
import { ThemePreferenceProvider } from './contexts/ThemeContext';
import { HomeDashboardProvider } from './contexts/HomeDashboardContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: CACHE_TIME.DEFAULT,
    },
  },
});

// Dev: expose queryClient for console access and Ctrl+Shift+Q to clear all caches
if (import.meta.env.DEV) {
  (window as any).__queryClient = queryClient;
  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'Q') {
      queryClient.clear();
      console.log('[DEV] All query caches cleared');
    }
  });
}

function LandingPageRedirect() {
  const { landingPage, isLoading } = useUserPreferences();
  if (isLoading) return null;
  if (landingPage && landingPage !== '/') {
    return <Navigate to={landingPage} replace />;
  }
  return <HomeDashboard />;
}

function AuthenticatedApp() {
  useEffect(() => {
    startOutboxWatchdog();
    void requestPersistentStorage();
  }, []);

  return (
    <UserPreferencesProvider>
      <ThemePreferenceProvider>
        <HomeDashboardProvider>
          <Layout>
            <Routes>
              {appRoutes.map((route, index) => (
                <Route key={index} path={route.path} element={route.element} />
              ))}
              <Route path="/" element={<LandingPageRedirect />} />
            </Routes>
          </Layout>
        </HomeDashboardProvider>
      </ThemePreferenceProvider>
    </UserPreferencesProvider>
  );
}

/**
 * A DATA router (createBrowserRouter + RouterProvider), not <BrowserRouter>.
 *
 * This is what makes the unsaved-changes guard possible: `useBlocker` (ViewToolbar) resolves its
 * router from `useDataRouterContext`, which only a RouterProvider supplies — under <BrowserRouter>
 * it throws. Without it the confirm dialog could only ever hang off the toolbar's own back button,
 * so leaving a dirty view via the main menu or the browser's back button silently discarded the work.
 *
 * The route tree itself is unchanged: `/*` still renders a descendant <Routes> inside
 * AuthenticatedApp. Descendant routes get no data APIs (loaders/actions) — we use none — but they
 * navigate through this router, so the blocker still sees and can stop them.
 *
 * Module scope on purpose: re-creating the router on every render would reset history state.
 */
const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  // Public auth-flow routes (code+PKCE callback + post-logout landing, D13b).
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/auth/logged-out', element: <LoggedOut /> },
  {
    path: '/*',
    element: (
      <ProtectedRoute>
        <AuthenticatedApp />
      </ProtectedRoute>
    ),
  },
]);

function App() {
  // Use cached accent color from localStorage to avoid accent flash on reload
  const initialTheme = useMemo(() => {
    const cachedAccent = localStorage.getItem('nexo.theme.accent') as AccentColorKey | null;
    return createAppTheme('light', cachedAccent || 'navy');
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={initialTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <QueryClientProvider client={queryClient}>
            {/* Both are app-wide and routerless on purpose: the session can end on any
                screen, and the activity gate must keep counting across navigation. */}
            <SessionActivityTracker />
            <SessionExpiryWarning />
            <SessionExpiredDialog />
            <RouterProvider router={router} />
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
